import { defineStore } from "pinia";
import { ref } from "vue";

import type { BoardType, IdMap } from "@/model/baseTypes";
import { isFlexBoard } from "@/model/board";
import type { AlmStickyType, StickyType } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

const { t } = useGlobalI18n();

export const useStickyTypeStore = defineStore(
  "stickyType",
  () => {
    const stickyTypes = ref<StickyType[]>([]);
    const almStickyTypes = ref<IdMap<AlmStickyType>>({});

    function nonFlexBoardStickyTypes(type: BoardType) {
      const originTypes = stickyTypes.value.filter((s) => s.origin === type);
      const mirrorTypes = stickyTypes.value.filter(
        (s) => s.origin !== type && s.usable.find((a) => a === type),
      );
      return boardStickyTypes(originTypes, mirrorTypes);
    }

    function flexBoardStickyTypes(flexType: string) {
      const originTypes = stickyTypes.value.filter(
        (s) => isFlexBoard(s.origin) && s.flexOrigin === flexType,
      );
      const mirrorTypes = stickyTypes.value.filter(
        (s) =>
          !isFlexBoard(s.origin) &&
          s.usable.find(isFlexBoard) &&
          (s.flexUsable || []).find((a) => a === flexType),
      );
      return boardStickyTypes(originTypes, mirrorTypes);
    }

    return {
      stickyTypes: asReadonly(stickyTypes),

      setStickyTypes: (types: StickyType[]) => (stickyTypes.value = types),
      setAlmStickyTypes: (types: IdMap<AlmStickyType>) => (almStickyTypes.value = types),

      findStickyType: (id: string) =>
        stickyTypes.value.find((t) => t.id === id) ?? zombieStickyType(),
      nonFlexBoardStickyTypes,
      flexBoardStickyTypes,

      findTypeByAlmType: (almTypeId: string) =>
        stickyTypes.value.find((t) => t.almType === almTypeId) ?? zombieStickyType(),
      almStickyTypeOf: function (stickyType: Pick<StickyType, "almType">) {
        return almStickyTypes.value[stickyType.almType || ""];
      },
      getAlmTypeIds: (stickyTypeIds: string[]) =>
        stickyTypeIds
          .map((id) => stickyTypes.value.find((t) => t.id === id)?.almType)
          .filter((almType): almType is string => !!almType),

      addStickyType: (stickyType: StickyType) => stickyTypes.value.push(stickyType),
      updateStickyType: (stickyType: StickyType) => {
        const i = stickyTypes.value.findIndex((type) => type.id === stickyType.id);
        if (i >= 0) {
          stickyTypes.value[i] = stickyType;
        }
      },
      removeStickyType: (id: string) =>
        (stickyTypes.value = stickyTypes.value.filter((type) => type.id !== id)),
    };
  },
  { autoReset: true },
);

function boardStickyTypes(origin: StickyType[], mirror: StickyType[]) {
  mirror.sort((a, b) => a.name.localeCompare(b.name));
  return [...origin, ...mirror];
}

function zombieStickyType(): StickyType {
  return {
    id: "0",
    name: t("stickyType.unknown"),
    almType: null,
    color: [0.5, 0.5, 0.5, 0.8],
    altColor: [0.5, 0.5, 0.5, 0.8],
    origin: "risk",
    flexOrigin: null,
    usable: [],
    flexUsable: [],
    functionality: "note",
  };
}

export function isZombieType(type: StickyType) {
  return type.id === "0";
}

export function isStickyTypeAlmSynced(type: StickyType) {
  return useStickyTypeStore().almStickyTypeOf(type)?.isMapped;
}
